import { UISimulation } from '@/components/help/UISimulation';

const D = 2100;

function Shell({
  step,
  title,
  children,
}: {
  step: number;
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="rounded-md border border-dash-border bg-dash-bg p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-mono uppercase tracking-wide text-dash-muted">
          Step {step} of 5
        </div>
        <div className="text-[11px] font-mono text-dash-secondary">{title}</div>
      </div>
      {children}
    </div>
  );
}

function SidebarFrame(): JSX.Element {
  return (
    <Shell step={1} title="Annotation sidebar">
      <ul className="space-y-1.5 text-[11px]">
        {[
          { kind: 'Inline', note: 'Cite the 6-8 week figure.' },
          { kind: 'Section', note: 'Add a real-world example.' },
          { kind: 'Component', note: 'Soften this FAQ answer.' },
        ].map((a) => (
          <li
            key={a.note}
            className="rounded border border-dash-border bg-dash-surface px-2 py-1.5"
          >
            <div className="flex items-center gap-2">
              <span className="rounded border border-purple-400/40 bg-purple-400/10 text-purple-300 px-1.5 py-0.5 text-[10px] font-mono">
                {a.kind}
              </span>
            </div>
            <div className="mt-1 text-dash-primary">{a.note}</div>
          </li>
        ))}
      </ul>
    </Shell>
  );
}

function ReviseFrame(): JSX.Element {
  return (
    <Shell step={2} title="Click Revise">
      <div className="rounded border border-violet-400/40 bg-violet-400/10 p-3 text-xs">
        <div className="text-violet-300 font-mono uppercase text-[10px]">
          3 unresolved
        </div>
        <div className="mt-1 text-dash-primary">
          Compile annotations into one writer request.
        </div>
        <div className="mt-2 inline-flex rounded bg-accent/15 text-accent border border-accent/40 px-2 py-1">
          Revise with AI
        </div>
      </div>
    </Shell>
  );
}

function ModalFrame(): JSX.Element {
  return (
    <Shell step={3} title="Modal loads">
      <div className="rounded border border-dash-border bg-dash-surface p-3 text-xs">
        <div className="text-[10px] font-mono uppercase text-dash-muted">
          User message
        </div>
        <pre className="mt-1 max-h-20 overflow-hidden rounded border border-dash-border bg-dash-bg p-2 text-[10px] text-dash-secondary leading-snug">
          Please address the following annotations:{'\n'}
          - "six to eight weeks" -- cite a study{'\n'}
          - Section "Safety" -- add a real-world example
        </pre>
        <div className="mt-2 text-[10px] font-mono uppercase text-warning">
          claude is writing…
        </div>
      </div>
    </Shell>
  );
}

function DiffFrame(): JSX.Element {
  return (
    <Shell step={4} title="Diff view">
      <div className="rounded border border-dash-border bg-dash-surface p-3 text-[11px] font-mono">
        <div className="text-error">
          - Espresso machines need a descale every six to eight weeks.
        </div>
        <div className="text-success">
          + Espresso machines need a descale every 6 to 8 weeks on city
        </div>
        <div className="text-success">
          + water (Smith et al., 2024).
        </div>
      </div>
    </Shell>
  );
}

function AcceptFrame(): JSX.Element {
  return (
    <Shell step={5} title="Accept">
      <div className="rounded border border-success/40 bg-success/10 p-3 text-xs">
        <div className="text-dash-primary">
          Body replaced. Bundled annotations resolved.
        </div>
        <div className="mt-1 text-[10px] font-mono uppercase text-success">
          3 done
        </div>
      </div>
    </Shell>
  );
}

export function RevisionSimulation(): JSX.Element {
  return (
    <UISimulation
      steps={[
        { frame: <SidebarFrame />, label: 'Annotations in the sidebar', duration: D },
        { frame: <ReviseFrame />, label: 'Bundle the notes', duration: D },
        { frame: <ModalFrame />, label: 'Modal compiles + calls Claude', duration: D },
        { frame: <DiffFrame />, label: 'Inline diff', duration: D },
        { frame: <AcceptFrame />, label: 'Accept swaps body_md', duration: D },
      ]}
    />
  );
}
